import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
} from "@mui/material";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import { Paper, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import YouTubeTitleGenContent from "../Contents/YtTitleGeneratorContent.js";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

const handleCopy = (text) => {
  navigator.clipboard.writeText(text);
};

function YouTubeTitleGenerator({ user, login, isLoggedIn }) {
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [titles, setTitles] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setKeyword(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleGenerateTitles = async () => {
    setError("");
    setSuccess("");
    setTitles([]);
    setDownloadUrl("");
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }
    if (!keyword || keyword.trim().length < 2) {
      return setError("Please enter a valid keyword or topic.");
    }
    if (credits.remaining <= 0) {
      setError("Not enough credits to generate titles.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/video-title-generator`,
        { youtubeTitleKeyword: keyword },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      setLoading(false);
      setSuccess(response.data.successMessage);
      setTitles(response.data.titles || []);

      // Update credits
      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });

      // Download CSV if available
      const fileName = response.data.fileName;
      if (fileName) {
        const downloadResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/youtube/video-title-generator/download`,
          { fileName },
          {
            withCredentials: true,
            responseType: "blob",
          }
        );
        const blob = new Blob([downloadResponse.data], {
          type: "application/octet-stream",
        });
        const downloadUrl = URL.createObjectURL(blob);
        setDownloadUrl(downloadUrl);

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.errorMessage ||
          "Failed to generate titles. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            YouTube Title Generator 🎯
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Generate SEO-friendly, clickable YouTube titles for your next video
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="Enter your video topic or keyword"
              variant="outlined"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g. How to grow a YouTube channel"
              size="medium"
              id="youtube-title-generator"
              name="primary-textbox"
            />
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
              {downloadUrl && (
                <>
                  <br />
                  If your download hasn't started,{" "}
                  <a href={downloadUrl} download="titles.csv">
                    click here
                  </a>
                </>
              )}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleGenerateTitles}
            >
              Generate Titles
            </StyledButton>
          </Box>

          {titles.length > 0 && (
            <Box mt={4}>
              <Typography
                variant="h4"
                gutterBottom
                align="center"
                sx={{
                  fontWeight: "bold",
                  background: "linear-gradient(90deg, #ff6a00, #ee0979)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.5px",
                  mb: 4,
                }}
              >
                🚀 Title Suggestions
              </Typography>

              <Box display="grid" gap={2}>
                {titles.map((t, index) => (
                  <Paper
                    key={index}
                    elevation={3}
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ mr: 2, flex: 1 }}>
                      {t.Titles}
                    </Typography>
                    <Tooltip title="Copy to clipboard">
                      <IconButton onClick={() => handleCopy(t.Titles)}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <b> 💡 1 Title search = 1 Credit</b>
              <br></br>Enter your focus keyword to get the YouTube title
              suggestions.Have questions?{" "}
              <Link to="/contact" color="primary">
                Contact us
              </Link>
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="youtube/video-title-generator"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeTitleGenContent />
      </PageContainer>
    </>
  );
}

export default YouTubeTitleGenerator;
